import {
  Button,
  IconRenderer,
  optionsAnimationClasses,
  optionsBaseClasses,
  optionsHiddenClasses,
  optionsHiddenClassesMobile,
  optionsMobileBaseClasses,
  optionsVisibleClasses,
  optionsVisibleClassesMobile,
} from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import { ReactElement, useState } from "react";

import { useThemeByPath } from "../hooks";
import { defaultLocales, Locale, LocaleCodeType } from "./locales";
import { Option } from "./Option";
import { OptionsList } from "./OptionsList";

const getDesktopVisibleClasses = (isOptionsVisible: boolean) => [
  ...optionsBaseClasses,
  ...optionsAnimationClasses,
  ...(isOptionsVisible
    ? [...optionsVisibleClasses, "opened"]
    : optionsHiddenClasses),
];

const getMobileVisibleClasses = (isOptionsVisible: boolean) => [
  ...optionsMobileBaseClasses,
  ...optionsAnimationClasses,
  ...(isOptionsVisible
    ? [...optionsVisibleClasses, "opened", ...optionsVisibleClassesMobile]
    : [...optionsHiddenClasses, ...optionsHiddenClassesMobile]),
];

export type LanguageSelectorProps<T = Locale> = {
  locales?: Locale[];
  currentLocale: LocaleCodeType;
  onChange: (params: T) => void;
  isMobileVersion?: boolean;
};

export const LanguageSelector = ({
  locales = Object.values(defaultLocales),
  currentLocale,
  onChange,
  isMobileVersion = false,
}: LanguageSelectorProps): ReactElement => {
  const { themeMode } = useThemeByPath();
  const [isOptionsVisible, setOptionsVisibility] = useState(false);
  const currentLocaleLabel =
    locales.find((locale) => locale.code === currentLocale)?.label ??
    currentLocale.toUpperCase();

  const optionsContainerClasses = clsx(
    isMobileVersion
      ? getMobileVisibleClasses(isOptionsVisible)
      : getDesktopVisibleClasses(isOptionsVisible),
  );

  const toggleOptionsVisibility = () => {
    setOptionsVisibility(!isOptionsVisible);
  };

  const buttonProps =
    themeMode === "light"
      ? { variant: "ps-btn-transparent" as const }
      : { className: "border border-blue-875 !text-white" };

  return (
    <div data-testid="language-selector">
      <Button
        type="button"
        size={isMobileVersion ? "ps-btn-sm" : "ps-btn-md"}
        onClick={toggleOptionsVisibility}
        aria-description={`Language selected: ${currentLocaleLabel}`}
        {...buttonProps}
      >
        <div className="flex items-center gap-2">
          <IconRenderer icon="world" size="inherit" aria-hidden />
          {currentLocaleLabel}
        </div>
      </Button>

      <div className={optionsContainerClasses}>
        <OptionsList
          isMobileVersion={isMobileVersion}
          toggleOptionsVisibility={toggleOptionsVisibility}
        >
          {locales.map((locale, index) => (
            <Option
              locale={locale}
              isSelected={locale.code === currentLocale}
              onChange={onChange}
              toggleOptionsVisibility={toggleOptionsVisibility}
              key={`${locale.code}-${index}`}
            />
          ))}
        </OptionsList>
      </div>
    </div>
  );
};
